<template>
  <app-page class="saldo-page" fill lean>
    <!-- Kopf wie bei Verkäufen und Zahlungen (Kundenwunsch 13.10.2026): leichter Titel, Aktionen mit Icon und
         Beschriftung (Handy: Icon-Knöpfe); Saldo ist immer nach Fahrer gruppiert, „Alle aufklappen“ daher nie gesperrt -->
    <template #actions>
      <template v-if="!handy">
        <app-button :icon="expanded ? 'fold' : 'unfold'" @click="collapseAllClick">{{ expanded ? 'Alle zuklappen' : 'Alle aufklappen' }}</app-button>
        <app-button icon="refresh" @click="refreshDataGrid">Aktualisieren</app-button>
        <app-button icon="download" @click="exportToExcel">Excel-Export</app-button>
      </template>
      <template v-else>
        <app-icon-button
          :icon="expanded ? 'fold' : 'unfold'"
          :label="expanded ? 'Alle Gruppen zuklappen' : 'Alle Gruppen aufklappen'"
          @click="collapseAllClick"
        />
        <app-icon-button icon="refresh" label="Aktualisieren" @click="refreshDataGrid" />
        <app-icon-button icon="download" label="Excel-Export" @click="exportToExcel" />
      </template>
    </template>
    <!-- Werkzeugleiste: links Zeitraum und Suche, rechts Filter (Panel wie bei Verkäufen statt der Filterzeile) -->
    <template #toolbar>
      <app-month-stepper v-model="month1" prefix="Von" />
      <app-month-stepper v-model="month2" prefix="Bis" />
      <app-search v-model="searchText" placeholder="Im Saldo suchen" @input="onSearchInput" />
    </template>
    <template #toolbar-end>
      <sale-filter-menu
        :value="filters"
        :facets="facets"
        :result-count="resultCount"
        :description="filterTooltip"
        :segment-defs="noSegments"
        :active-filters="activeFilterCount"
        range-label="Saldo"
        :result-nouns="resultNouns"
        :empty-value="emptySaldoFilters"
        :align-right="!handy"
        @open="refreshFacets"
        @input="onFiltersInput"
      />
    </template>

    <!-- Meldung liegt fest über der Seite und nimmt keinen Platz im Inhalt ein -->
    <!-- Toast unten rechts wie bei PayPal (Kundenwunsch 13.10.2026) -->
    <app-toast
      v-model="snackbar"
      icon="printer"
      text="Quittung wird generiert und heruntergeladen …"
      indeterminate
      closable
    />

    <!-- Tabelle füllt die Höhe über der Fußleiste (auf dem Handy natürliche Höhe) -->
    <div class="app-grid-fill saldo-grid" :class="{ 'saldo-grid--auto': handy }">
      <dx-data-grid
        id="paymentsDataGrid"
        :ref="gridRefName"
        :height="gridHeight"
        :data-source="devicesDataSource"
        :show-borders="false"
        :show-column-lines="false"
        :show-row-lines="true"
        :column-auto-width="true"
        :allow-column-reordering="!gridTouch"
        :allow-column-resizing="!gridTouch"
        :selection="selection"
        :hover-state-enabled="!gridTouch"
        :element-attr="gridAttr"
        @cell-prepared="cellPrepared"
        @content-ready="onContentReady"
        @toolbar-preparing="onToolbarPreparing($event)"
        :columns="columns"
        :scrolling="scrolling"
      >
        <dx-group-panel :visible="false" />
        <dx-state-storing
          :enabled="true"
          type="custom"
          storage-key="saldoStorage"
          :customLoad="customLoad"
          :customSave="customSave"
        />
        <dx-grouping :auto-expand-all="expanded" expandMode="rowClick" />
        <dx-paging :page-size="defaultPageSize" />
        <!-- Seitenwahl und Blättern in der Fußleiste (AppGridFooter) -->
        <dx-pager :visible="false" />
        <dx-export
          :enabled="false"
          :allow-export-selected-data="true"
          file-name="Zahlungen"
          :texts="{ exportAll: 'Alle Saldo herunterladen', exportSelectedRows: 'Ausgewählte Saldo herunterladen', exportTo: 'Excel Download'}"
        />

        <dx-summary>
          <dx-group-item
            :align-by-column="false"
            :show-in-group-footer="true"
            column="diff_amount"
            summary-type="sum"
            display-format="Saldo: {0}"
            value-format="#######0.00 €"
          />
          <dx-group-item
            :align-by-column="false"
            :show-in-group-footer="true"
            column="sale_amount_cash"
            summary-type="sum"
            display-format="Bar: {0}"
            value-format="#######0.00 €"
          />
          <dx-group-item
            :align-by-column="false"
            :show-in-group-footer="true"
            column="sale_amount_sumup"
            summary-type="sum"
            display-format="Kartenzahlung: {0}"
            value-format="#######0.00 €"
          />
          <dx-group-item
            :align-by-column="false"
            :show-in-group-footer="true"
            column="sale_amount_voucher"
            summary-type="sum"
            display-format="Gutschein: {0}"
            value-format="#######0.00 €"
          />
          <dx-group-item
            :align-by-column="false"
            :show-in-group-footer="true"
            column="sale_amount"
            summary-type="sum"
            display-format="Gesamt: {0}"
            value-format="#######0.00 €"
          />
          <dx-group-item
            :align-by-column="false"
            :show-in-group-footer="true"
            column="pay_amount"
            summary-type="sum"
            display-format="Zahlungen: {0}"
            value-format="#######0.00 €"
          />
          <dx-group-item
            :align-by-column="false"
            :show-in-group-footer="false"
            column="diff_amount"
            summary-type="sum"
            display-format="Saldo: {0}"
            value-format="#######0.00 €"
          />

          <dx-total-item
            column="sale_amount"
            summary-type="sum"
            display-format="Gesamtverkäufe: {0}"
            value-format="#######0.00 €"
          />

          <dx-total-item
            column="pay_amount"
            summary-type="sum"
            display-format="Gesamtzahlungen: {0}"
            value-format="#######0.00 €"
          />

          <dx-total-item
            column="diff_amount"
            summary-type="sum"
            display-format="Gesamtsaldo: {0}"
            value-format="#######0.00 €"
          />
        </dx-summary>
        <!-- <template #userGroupTemplate="data">
          <div style="margin: 10px !important;">
            <dx-check-box class="group-by-checkbox" :on-click="onCheckboxRendered"></dx-check-box>
            {{data.text}}
            <span
              class="ml-3"
            >Benutzername: {{data.data.displayValue }} (Saldo: {{data.data.summaryItems[0].value.toFixed(2).toString().replace('.', ',')}} €)</span>
          </div>
        </template>-->
      </dx-data-grid>
    </div>
    <app-grid-footer ref="fuss" :page-sizes="pageSizes">
      <template #summary>
        <span>Verkäufe <strong>{{ euro(summary.sales) }}</strong></span>
        <span class="app-sep" aria-hidden="true">·</span>
        <span>Zahlungen <strong>{{ euro(summary.payments) }}</strong></span>
        <span class="app-sep" aria-hidden="true">·</span>
        <span>Saldo <strong>{{ euro(summary.diff) }}</strong></span>
      </template>
    </app-grid-footer>
  </app-page>
</template>

<script>
import {
  DxDataGrid,
  DxPaging,
  DxPager,
  DxExport,
  DxGrouping,
  DxGroupPanel,
  DxGroupItem,
  DxSummary,
  DxTotalItem,
  DxStateStoring
} from "devextreme-vue/data-grid";
// import { DxCheckBox } from "devextreme-vue/check-box";
import deMessages from "devextreme/localization/messages/de.json";
import { locale, loadMessages } from "devextreme/localization";
import CustomStore from "devextreme/data/custom_store";
import axios from "axios";
import { loadSaldoMonths } from "@/utils/saldoMonths";
import printJS from "print-js";
import moment from "moment";
import { hasPermission } from "@/auth/auth";
import DataGridMixin from "@/mixins/DataGridMixin";
import query from "devextreme/data/query";
import { formatEuro } from "@/utils/format";
import SaleFilterMenu from "@/components/sales/SaleFilterMenu";
import {
  emptyFilters,
  activeCount,
  buildPredicate,
  buildFacets,
  describeFilters,
  NO_VALUE_TEXT
} from "@/components/sales/saleFilters";
import { SALDO_FILTER_CONFIG } from "@/components/payments/paymentFilters";

const RESULT_NOUNS = ["Eintrag", "Einträge"];
const NO_SEGMENTS = [];

export default {
  components: {
    DxDataGrid,
    DxPaging,
    DxPager,
    DxExport,
    DxGrouping,
    DxGroupPanel,
    DxGroupItem,
    DxSummary,
    DxTotalItem,
    DxStateStoring,
    SaleFilterMenu
    // DxCheckBox
  },
  mixins: [DataGridMixin],
  computed: {
    handy() {
      return !!(this.$dichte && this.$dichte.handy);
    },
    // Handy: AppPage fill wirkt dort nicht, das Grid wächst mit seinen Zeilen; sonst füllt es die Höhe
    gridHeight() {
      return this.handy ? undefined : "100%";
    },
    activeFilterCount() {
      return activeCount(this.filters, SALDO_FILTER_CONFIG);
    },
    filterTooltip() {
      const parts = describeFilters(this.filters, this.filterLabel, 3, SALDO_FILTER_CONFIG);
      return parts.length ? parts.join(" · ") : null;
    },
    dataUrl() {
      return `${
        this.$store.state.baseUrl
      }saldo?from_month=${this.month1.substring(
        5,
        7
      )}&from_year=${this.month1.substring(
        0,
        4
      )}&to_month=${this.month2.substring(
        5,
        7
      )}&to_year=${this.month2.substring(0, 4)}`;
    },
    columns() {
      return [
        {
          dataField: "user_id",
          caption: "Fahrer",
          sortIndex: 0,
          lookup: {
            dataSource: this.driver,
            valueExpr: "user_id",
            displayExpr: "fullname"
          }
          // groupCellTemplate: "userGroupTemplate"
        },
        {
          dataField: "user_id",
          caption: "Fahrer",
          groupIndex: 0,
          name: "user_id_grouping",
          lookup: {
            dataSource: this.driver,
            valueExpr: "user_id",
            displayExpr: "fullname"
          }
          // groupCellTemplate: "userGroupTemplate"
        },
        {
          dataField: "date",
          caption: "Datum",
          dataType: "date",
          sortIndex: 1,
          sortOrder: "asc"
        },
        {
          dataField: "sale_amount_cash",
          caption: "Verkäufe(Bar)",
          alignment: "right",
          dataType: "number",
          format: "######0.00 €"
        },
        {
          dataField: "sale_amount_sumup",
          caption: "Verkäufe(Kartenzahlung)",
          alignment: "right",
          dataType: "number",
          format: "######0.00 €"
        },
        {
          // Gutscheine der Drucker-App (Backend seit Oktober 2026): kein Bargeld, zählen nicht zu Bar und damit nicht
          // zum Saldo (Zahlungen − Bar)
          dataField: "sale_amount_voucher",
          caption: "Verkäufe(Gutschein)",
          alignment: "right",
          dataType: "number",
          format: "######0.00 €"
        },
        {
          dataField: "sale_amount",
          caption: "Verkäufe(gesamt)",
          alignment: "right",
          dataType: "number",
          format: "######0.00 €"
        },
        {
          dataField: "sale_count",
          caption: "Anzahl Verkäufe"
        },
        {
          dataField: "pay_amount",
          caption: "Zahlungen",
          alignment: "right",
          dataType: "number",
          format: "######0.00 €"
        },
        {
          dataField: "diff_amount",
          caption: "Saldo",
          alignment: "right",
          dataType: "number",
          format: "######0.00 €"
        },
        {
          dataField: "pay_comment",
          caption: "Bemerkung"
        },
        {
          dataField: "pay_place",
          caption: "Bezahlt wo"
        },
         {
          dataField: "billing_month",
          caption: "Abrechnungsmonat"
        },
         {
          dataField: "billing_year",
          caption: "Abrechnungsjahr"
        }
      ];
    }
  },
  created() {
    loadMessages(deMessages);
    locale("de"); // immer Deutsch wie src/plugins/devextreme.js (vorher Browsersprache)
  },
  data() {
    return {
      storageKey: "saldoStorage",
      customer: this.$store.state.customerId,
      user: this.$store.state.cognitoUsername,
      month1: new Date(new Date().getFullYear(), 0, 2)
        .toISOString()
        .substr(0, 7),
      month2: new Date().toISOString().substr(0, 7),
      driver: [],
      gridRefName: "paymentsDataGrid",
      // Filter-Panel (Kundenwunsch 13.10.2026) und Summen der Fußleiste nach Filter und Suche
      filters: emptyFilters(SALDO_FILTER_CONFIG),
      facets: [],
      resultCount: null,
      summary: { sales: 0, payments: 0, diff: 0 },
      noSegments: NO_SEGMENTS,
      resultNouns: RESULT_NOUNS,
      search: "",
      searchText: "",
      autoExpandAll: false,
      snackbar: false,
      expanded: false,
      // Auswahl einzeln (L9: verschachteltes multiple entfernt); Objekt-Optionen nicht als Literal im Template
      selection: { mode: "single" },
      gridAttr: {},
      devicesDataSource: new CustomStore({
        // geladene Zeilen merken: Werte der Filterlisten und Summen (nicht reaktiv)
        // je Monat eine Anfrage, parallel (src/utils/saldoMonths.js; 13.10.2026: Januar bis Oktober lief bei großen
        // Mandanten in das 29-s-Limit des API Gateway, die Seite blieb bei „Loading …“); dataUrl bleibt der Auslöser
        load: () => loadSaldoMonths(
          this.$store.state.baseUrl,
          this.month1.substring(0, 4), this.month1.substring(5, 7),
          this.month2.substring(0, 4), this.month2.substring(5, 7)
        ).then(rows => {
          this._rows = rows;
          this.refreshFacets();
          return rows;
        })
      })
    };
  },
  watch: {
    dataUrl: function() {
      this.refreshDataGrid();
    }
  },
  methods: {
    euro(value) {
      return formatEuro(value || 0);
    },
    // ---------------------------------------------------------------- Filter-Panel (Kundenwunsch 13.10.2026)
    emptySaldoFilters() {
      return emptyFilters(SALDO_FILTER_CONFIG);
    },
    filterLabel(listKey, key) {
      if (key === "") {
        return NO_VALUE_TEXT;
      }
      if (listKey === "drivers") {
        const user = this.driver.find(d => String(d.user_id) === key);
        return (user && (user.fullname || user.username)) || key;
      }
      return key;
    },
    refreshFacets() {
      this.facets = Object.freeze(buildFacets(this._rows || [], this.filters, this.filterLabel, SALDO_FILTER_CONFIG));
    },
    // Filter als Prüffunktion auf der Datenquelle wie bei Verkäufen (jedes Mal eine neue Funktion)
    onFiltersInput(filters) {
      this.filters = filters || this.emptySaldoFilters();
      const grid = this.$refs[this.gridRefName] && this.$refs[this.gridRefName].instance;
      if (grid) {
        const match = buildPredicate(this.filters, SALDO_FILTER_CONFIG);
        grid.filter(match ? [match, "=", true] : null);
      }
      this.refreshFacets();
    },
    // Fußleiste: Summen der Zeilen nach Filter und Suche, Seitenwahl und Blättern
    onContentReady(e) {
      const grid = e.component;
      const count = Number(grid.totalCount());
      this.resultCount = count >= 0 ? count : 0;
      const rows = this._rows || [];
      const filter = grid.getCombinedFilter();
      const visible = filter && rows.length ? query(rows).filter(filter).toArray() : rows;
      const sum = field => visible.reduce((total, row) => total + (Number(row[field]) || 0), 0);
      this.summary = { sales: sum("sale_amount"), payments: sum("pay_amount"), diff: sum("diff_amount") };
      if (this.$refs.fuss) {
        this.$refs.fuss.update(grid);
      }
    },
    customLoad: function () {
        var state = localStorage.getItem(this.storageKey);
        if (state) {
            state = JSON.parse(state);
            // Ein gespeicherter Zustand aus einer aelteren Spaltenkonfiguration
            // kann zwei Eintraege mit demselben Namen enthalten. DevExtreme
            // bricht dann mit E1059 ab und rendert das Grid gar nicht mehr -
            // die Daten kommen an, die Seite bleibt aber leer. In dem Fall
            // lieber den Zustand verwerfen als die Seite zu blockieren.
            var columns = state.columns || [];
            var names = columns.map(function (column) {
                return column.name || column.dataField;
            });
            if (new Set(names).size !== names.length) {
                localStorage.removeItem(this.storageKey);
                return null;
            }
            for (var i = 0; i < columns.length; i++) {
                columns[i].filterValue = null;
            }
        }
        return state;
    },
    customSave: function (state) {  
        localStorage.setItem(this.storageKey, JSON.stringify(state));  
    },  
    onCheckboxRendered() {},
    hasPermission(permissionName) {
      return hasPermission(permissionName);
    },
    printReceipt(event) {
      this.snackbar = true;
      const data = event.row.data;
      data.user = this.driver.filter(user => user.user_id === 51)[0];
      data.formatted_pay_date = moment(data.pay_date).format("DD.MM.YYYY");
      data.current_date = moment().format("DD.MM.YYYY");
      axios
        .post(
          `${this.$store.state.baseUrl}document?template=PaymentReceiptTemplate.docx`,
          data
        )
        .then(response => {
          printJS(response.data.url);
          this.snackbar = false;
        });
    },
    async loadDriver() {
      this.driver = (
        await axios.get(
          `${this.$store.state.baseUrl}user?customer_id=${this.$store.state.customerId}`
        )
      ).data;
    },
    cellPrepared(e) {
      if (e.rowType === "data") {
        if (e.data.datum < new Date(2019, 11, 12)) {
          e.cellElement.style.color = "#AAAAAA";
        }
        if (e.data.betrag > 5) {
          if (e.column.dataField === "sale_id") {
            e.cellElement.style.fontWeight = "bold";
          }
          if (e.column.dataField === "price") {
            e.cellElement.style.backgroundColor = "#FFBB00";
            e.cellElement.style.color = "#000000";
          }
        }
      }

      if (e.rowType === "groupFooter" && e.column.dataField === "betrag") {
        e.cellElement.style.fontStyle = "italic";
      }
    },
    refreshDataGrid() {
      this.$refs[this.gridRefName].instance.refresh();
    },
    collapseAllClick() {
      this.expanded = !this.expanded;
    },
    onSearchInput() {
      this.$refs[this.gridRefName].instance.searchByText(this.searchText);
    },
    exportToExcel() {
      this.$refs[this.gridRefName].instance.exportToExcel(false);
    },
    onBtnOverview() {
      this.autoExpandAll = false;
      this.$refs[this.gridRefName].instance.clearGrouping();
    },
    onBtnDriver() {
      this.autoExpandAll = false;
      this.$refs[this.gridRefName].instance.clearGrouping();
      this.$refs[this.gridRefName].instance.columnOption(
        "user_id",
        "groupIndex",
        0
      );
    },
    onToolbarPreparing() {
      // e.toolbarOptions.items.unshift(
      //   {
      //     location: "before",
      //     widget: "dxSelectBox",
      //     options: {
      //       width: 200,
      //       items: [
      //         {
      //           value: null,
      //           text: "Keine Gruppierung"
      //         },
      //         {
      //           value: "user",
      //           text: "Gruppierung nach Benutzer"
      //         }
      //       ],
      //       displayExpr: "text",
      //       valueExpr: "value",
      //       value: null,
      //       onValueChanged: this.groupChanged
      //     }
      //   },
      //   {
      //     location: "after",
      //     widget: "dxButton",
      //     options: {
      //       icon: this.expanded ? "collapse" : "expand",
      //       hint: this.expanded ? "Einklappen" : "Ausklappen",
      //       onClick: this.collapseAllClick
      //     }
      //   },
      //   {
      //     location: "after",
      //     widget: "dxButton",
      //     options: {
      //       icon: "refresh",
      //       hint: "Neu laden",
      //       onClick: this.refreshDataGrid
      //     }
      //   }
      // );
    },
    groupChanged(e) {
      switch (e.value) {
        case "user":
          this.onBtnDriver();
          break;
        default:
          this.onBtnOverview();
      }
    }
  },
  mounted() {
    this.loadDriver();
  }
};
</script>

<style scoped>
/* Handy: Grid in natürlicher Höhe */
.saldo-grid.saldo-grid--auto {
  flex: none;
}
/* Gesamtsummen stehen in der Fußleiste (AppGridFooter); die Summenzeile des Grids bleibt für den Excel-Export */
.saldo-page ::v-deep .dx-datagrid-total-footer {
  display: none;
}
.group-by-checkbox .dx-checkbox-icon {
  border: 1px solid white;
}
</style>
